import { visit } from 'unist-util-visit';
import type { Plugin } from 'unified';
import type { Root, Text } from 'mdast';

// Define the reference node type for the AST
interface ReferenceNode {
  type: 'reference';
  name: string;
  url?: string;
  data?: {
    hName: string;
    hProperties: Record<string, string | boolean | undefined>;
  };
}

// Extend the mdast types to include our custom reference node
declare module 'mdast' {
  interface StaticPhyloMap {
    reference: ReferenceNode;
  }
}

/**
 * Remark plugin to parse reference tags like <reference name="docs" url="..." />
 * and convert them to custom AST nodes that can be rendered by ReactMarkdown
 */
const remarkReference: Plugin<[], Root> = () => {
  return (tree: Root) => {
    // Process HTML nodes (references are parsed as HTML by ReactMarkdown)
    visit(tree, 'html', (node: { value: string }, index, parent) => {
      const html = node.value;
      const referenceRegex = /<reference\s+name="([^"]+)"(?:\s+url="([^"]*)")?\s*\/>/g;

      let match;
      const newNodes: Array<{ type: 'html'; value: string } | ReferenceNode> = [];
      let lastIndex = 0;

      // Find all reference tags in the HTML
      while ((match = referenceRegex.exec(html)) !== null) {
        const [fullMatch, name, url] = match;
        const matchStart = match.index!;
        const matchEnd = matchStart + fullMatch.length;

        // Add HTML before the reference (if any)
        if (matchStart > lastIndex) {
          const beforeHtml = html.slice(lastIndex, matchStart);
          if (beforeHtml) {
            newNodes.push({
              type: 'html',
              value: beforeHtml
            });
          }
        }

        // Add the reference node
        const referenceNode: ReferenceNode = {
          type: 'reference',
          name,
          url: url || undefined,
          data: {
            hName: 'span',
            hProperties: {
              'data-reference': true,
              'data-name': name,
              'data-url': url || undefined
            }
          }
        };

        newNodes.push(referenceNode);
        lastIndex = matchEnd;
      }

      // If we found any references, replace the HTML node
      if (newNodes.length > 0) {
        // Add any remaining HTML after the last reference
        if (lastIndex < html.length) {
          const afterHtml = html.slice(lastIndex);
          if (afterHtml) {
            newNodes.push({
              type: 'html',
              value: afterHtml
            });
          }
        }

        // Replace the original HTML node with the new nodes
        if (parent && typeof index === 'number') {
          parent.children.splice(index, 1, ...(newNodes as never[]));
        }
      }
    });

    // Also process text nodes for any references that might appear there
    visit(tree, 'text', (node: Text, index, parent) => {
      const text = node.value;
      const referenceRegex = /<reference\s+name="([^"]+)"(?:\s+url="([^"]*)")?\s*\/>/g;

      let match;
      const newNodes: Array<Text | ReferenceNode> = [];
      let lastIndex = 0;

      // Find all reference tags in the text
      while ((match = referenceRegex.exec(text)) !== null) {
        const [fullMatch, name, url] = match;
        const matchStart = match.index!;
        const matchEnd = matchStart + fullMatch.length;

        // Add text before the reference (if any)
        if (matchStart > lastIndex) {
          const beforeText = text.slice(lastIndex, matchStart);
          if (beforeText) {
            newNodes.push({
              type: 'text',
              value: beforeText
            });
          }
        }

        // Add the reference node
        const referenceNode: ReferenceNode = {
          type: 'reference',
          name,
          url: url || undefined,
          data: {
            hName: 'span',
            hProperties: {
              'data-reference': true,
              'data-name': name,
              'data-url': url || undefined
            }
          }
        };

        newNodes.push(referenceNode);
        lastIndex = matchEnd;
      }

      // If we found any references, replace the text node
      if (newNodes.length > 0) {
        // Add any remaining text after the last reference
        if (lastIndex < text.length) {
          const afterText = text.slice(lastIndex);
          if (afterText) {
            newNodes.push({
              type: 'text',
              value: afterText
            });
          }
        }

        // Replace the original text node with the new nodes
        if (parent && typeof index === 'number') {
          parent.children.splice(index, 1, ...(newNodes as never[]));
        }
      }
    });
  };
};

export default remarkReference;